import type {
  CourseBlock,
  LessonBlockContentPatch,
  LessonBlockCreateRequest,
  LessonBlockCreateResponse,
  LessonBlockMoveRequest,
  LessonBlockMutationResponse,
  LessonMetadataTranslationPatch,
  LessonMetadataTranslationProjection,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import {
  createLessonBlock,
  deleteLessonBlock,
  moveLessonBlock,
  patchLessonBlockContent,
} from '../../api/lessonBlockApi';
import { patchLessonMetadata } from '../../api/lessonMetadataApi';
import type { ContentState, LessonMeta } from '../../store/editorStore';
import { stripResolvedQuestionFields } from '../../store/serialization';

const clone = <T,>(value: T): T => structuredClone(value);
const equal = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);
const isTemporaryId = (blockId: string): boolean => blockId.startsWith('tmp_');

export interface GranularLessonApi {
  patchContent: (
    lessonId: string,
    blockId: string,
    patch: LessonBlockContentPatch,
    accountId: number | null,
  ) => Promise<LessonBlockMutationResponse>;
  create: (
    lessonId: string,
    request: LessonBlockCreateRequest,
    accountId: number | null,
  ) => Promise<LessonBlockCreateResponse>;
  move: (
    lessonId: string,
    blockId: string,
    request: LessonBlockMoveRequest,
    accountId: number | null,
  ) => Promise<LessonBlockMutationResponse>;
  delete: (
    lessonId: string,
    blockId: string,
    accountId: number | null,
  ) => Promise<LessonBlockMutationResponse>;
  patchMetadata: (
    lessonId: string,
    patch: LessonMetadataTranslationPatch,
    locale: string,
    accountId: number | null,
  ) => Promise<LessonMetadataTranslationProjection>;
}

const defaultApi: GranularLessonApi = {
  patchContent: patchLessonBlockContent,
  create: createLessonBlock,
  move: moveLessonBlock,
  delete: deleteLessonBlock,
  patchMetadata: patchLessonMetadata,
};

function buildMetadataPatch(
  baseline: LessonMeta,
  current: LessonMeta,
): LessonMetadataTranslationPatch {
  const patch: LessonMetadataTranslationPatch = {};
  if (baseline.title !== current.title) patch.title = current.title;
  if (baseline.description !== current.description) patch.description = current.description;
  if ((baseline.estimatedTimeMinutes ?? null) !== (current.estimatedTimeMinutes ?? null)) {
    patch.estimatedTimeMinutes = current.estimatedTimeMinutes ?? null;
  }
  if ((baseline.themeColor ?? null) !== (current.themeColor ?? null)) {
    patch.themeColor = current.themeColor ?? null;
  }
  return patch;
}

function remapContentState(
  content: ContentState,
  mapping: Record<string, string>,
): ContentState {
  if (Object.keys(mapping).length === 0) return content;
  const blocks: Record<string, CourseBlock> = {};
  for (const [blockId, block] of Object.entries(content.blocks)) {
    const nextId = mapping[blockId] ?? blockId;
    blocks[nextId] = { ...block, id: nextId } as CourseBlock;
  }
  return {
    ...content,
    blocks,
    blockOrder: content.blockOrder.map((blockId) => mapping[blockId] ?? blockId),
  };
}

export interface PersistGranularLessonOptions {
  lessonId: string;
  accountId: number | null;
  baseline: ContentState;
  current: ContentState;
  replacementActions?: Record<string, TranslationReplacementAction>;
  /** Duplicate block id -> source block id, from `editorStore.duplicateSourceByBlockId`. */
  duplicateSourceByBlockId?: Record<string, string>;
  api?: GranularLessonApi;
  onIdMapping?: (mapping: Record<string, string>) => void;
  onBlockContentSaved?: (
    blockId: string,
    response: LessonBlockMutationResponse,
    replacementAction: TranslationReplacementAction | undefined,
  ) => void;
  onBlockReconciled?: (blockId: string, submitted: CourseBlock, saved: CourseBlock) => void;
}

interface GranularPersistenceContext {
  lessonId: string;
  accountId: number | null;
  baseline: ContentState;
  replacementActions: Record<string, TranslationReplacementAction>;
  duplicateSourceByBlockId: Record<string, string>;
  api: GranularLessonApi;
  onIdMapping?: (mapping: Record<string, string>) => void;
  onBlockContentSaved?: PersistGranularLessonOptions['onBlockContentSaved'];
  onBlockReconciled?: PersistGranularLessonOptions['onBlockReconciled'];
}

interface GranularPersistenceState {
  current: ContentState;
  persistedOrder: string[];
}

async function persistMetadataPhase(
  context: GranularPersistenceContext,
  state: GranularPersistenceState,
): Promise<void> {
  const metadataPatch = buildMetadataPatch(
    context.baseline.lessonMeta,
    state.current.lessonMeta,
  );
  if (Object.keys(metadataPatch).length > 0) {
    await context.api.patchMetadata(
      context.lessonId,
      metadataPatch,
      'en',
      context.accountId,
    );
  }
}

async function persistChangedBlockContentPhase(
  context: GranularPersistenceContext,
  state: GranularPersistenceState,
): Promise<void> {
  for (const blockId of state.current.blockOrder) {
    if (isTemporaryId(blockId)) continue;
    const block = state.current.blocks[blockId];
    if (!block) continue;
    const previous = context.baseline.blocks[blockId];
    if (!previous || equal(previous, block)) continue;
    const replacementAction = context.accountId === null
      ? context.replacementActions[blockId]
      : undefined;
    // Only the wire copy is stripped — `block` itself stays whole so the
    // change comparison above, the store, and `onBlockReconciled`'s
    // submitted-vs-saved check all keep seeing the block the author edited.
    const response = await context.api.patchContent(
      context.lessonId,
      blockId,
      { block: stripResolvedQuestionFields(block), ...(replacementAction ? { replacementAction } : {}) },
      context.accountId,
    );
    state.persistedOrder = response.blockOrder;
    if (block.customizationState === 'inherited' && response.block) {
      state.current.blocks[blockId] = response.block;
      context.onBlockReconciled?.(blockId, block, response.block);
    }
    context.onBlockContentSaved?.(blockId, response, replacementAction);
  }
}

async function persistDeletedBlocksPhase(
  context: GranularPersistenceContext,
  state: GranularPersistenceState,
): Promise<void> {
  const removedBlockIds = context.baseline.blockOrder.filter((blockId) =>
    !isTemporaryId(blockId) && !(blockId in state.current.blocks));
  for (const blockId of removedBlockIds) {
    const response = await context.api.delete(
      context.lessonId,
      blockId,
      context.accountId,
    );
    state.persistedOrder = response.blockOrder;
  }
}

async function persistNewBlocksPhase(
  context: GranularPersistenceContext,
  state: GranularPersistenceState,
): Promise<void> {
  const newBlockIds = state.current.blockOrder.filter(
    (blockId) => !(blockId in context.baseline.blocks),
  );
  for (const newBlockId of newBlockIds) {
    const block = state.current.blocks[newBlockId];
    if (!block) continue;
    const targetIndex = state.current.blockOrder.indexOf(newBlockId);
    let clientBlockId = newBlockId;
    if (!isTemporaryId(clientBlockId)) {
      clientBlockId = `tmp_restore_${newBlockId}`;
      while (clientBlockId in state.current.blocks) clientBlockId += '_';
    }
    // A duplicate of a source that is itself still unsaved (temp id) can't
    // carry a translation yet regardless — translations can only be written
    // against an already-persisted block — so there is nothing to copy and
    // the marker is simply omitted from the create request.
    const sourceBlockId = context.duplicateSourceByBlockId[newBlockId];
    const response = await context.api.create(
      context.lessonId,
      {
        clientBlockId,
        block: stripResolvedQuestionFields(block),
        targetIndex,
        ...(sourceBlockId && !isTemporaryId(sourceBlockId) ? { sourceBlockId } : {}),
      },
      context.accountId,
    );
    const mapping = { [newBlockId]: response.blockId };
    context.onIdMapping?.(mapping);
    state.current = remapContentState(state.current, mapping);
    const submittedBlock = {
      ...block,
      id: response.blockId,
    } as CourseBlock;
    state.current.blocks[response.blockId] = response.block;
    context.onBlockReconciled?.(response.blockId, submittedBlock, response.block);
    state.persistedOrder = response.blockOrder;
  }
}

async function reconcileBlockOrderPhase(
  context: GranularPersistenceContext,
  state: GranularPersistenceState,
): Promise<void> {
  const desiredOrder = state.current.blockOrder;
  for (let targetIndex = 0; targetIndex < desiredOrder.length; targetIndex += 1) {
    const desiredBlockId = desiredOrder[targetIndex];
    if (!desiredBlockId || state.persistedOrder[targetIndex] === desiredBlockId) continue;

    let blockIdToMove = desiredBlockId;
    let blockTargetIndex = targetIndex;

    if (context.accountId !== null && state.current.blocks[desiredBlockId]?.clientAdded !== true) {
      // Client context can move only client-created blocks. When one moves
      // downward past base content, the first mismatch is the base block that
      // should fill its old slot. Move the displaced client block to its
      // desired index instead of incorrectly asking the API to move that base
      // block.
      const displacedBlockId = state.persistedOrder[targetIndex];
      const displacedTargetIndex = displacedBlockId
        ? desiredOrder.indexOf(displacedBlockId)
        : -1;
      if (
        displacedBlockId
        && state.current.blocks[displacedBlockId]?.clientAdded === true
        && displacedTargetIndex >= 0
      ) {
        blockIdToMove = displacedBlockId;
        blockTargetIndex = displacedTargetIndex;
      }
    }

    const response = await context.api.move(
      context.lessonId,
      blockIdToMove,
      { targetIndex: blockTargetIndex },
      context.accountId,
    );
    state.persistedOrder = response.blockOrder;
  }
}

export async function persistGranularLessonSnapshot({
  lessonId,
  accountId,
  baseline,
  current,
  replacementActions = {},
  duplicateSourceByBlockId = {},
  api = defaultApi,
  onIdMapping,
  onBlockContentSaved,
  onBlockReconciled,
}: PersistGranularLessonOptions): Promise<ContentState> {
  const context: GranularPersistenceContext = {
    lessonId,
    accountId,
    baseline,
    replacementActions,
    duplicateSourceByBlockId,
    api,
    onIdMapping,
    onBlockContentSaved,
    onBlockReconciled,
  };
  const state: GranularPersistenceState = {
    current: clone(current),
    persistedOrder: [...baseline.blockOrder],
  };

  await persistMetadataPhase(context, state);
  await persistChangedBlockContentPhase(context, state);
  await persistDeletedBlocksPhase(context, state);
  await persistNewBlocksPhase(context, state);
  await reconcileBlockOrderPhase(context, state);

  return state.current;
}
